import React from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "@homesusa/auth";

export const REAUTH_PARAM = "reauth";

// The impersonation stop returns the browser to the destination application with `?reauth=1`.
// The cached OIDC session may still belong to the impersonated user, so the marker forces a
// full sign-in instead of letting the private routes silently renew the stale identity.
export function hasReauthMarker(search: string = window.location.search): boolean {
  return new URLSearchParams(search).get(REAUTH_PARAM) === "1";
}

/**
 * Drop the cached OIDC session so a stale identity cannot be reused. Storage is best-effort:
 * blocked cookies or private mode must never prevent booting.
 */
export function discardCachedOidcSession(): void {
  try {
    for (const store of [window.sessionStorage, window.localStorage]) {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (key && key.startsWith("oidc.")) {
          keys.push(key);
        }
      }
      keys.forEach((key) => store.removeItem(key));
    }
  } catch (error) {
    console.error("The cached OIDC session could not be discarded:", error);
  }
}

/**
 * Remove the reauth marker from the address bar so it does not stick around if the browser
 * comes back to this entry after the identity provider round-trip.
 */
export function stripReauthMarker(): void {
  const params = new URLSearchParams(window.location.search);
  params.delete(REAUTH_PARAM);
  const query = params.toString();
  const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ""}${
    window.location.hash
  }`;
  window.history.replaceState(null, "", cleanUrl);
}

/**
 * Layout route that forces a full sign-in when the app is opened with the impersonation
 * `?reauth=1` marker. Without the marker it only renders the nested routes, so the normal flow
 * is unchanged. If the sign-in request cannot be started, it reports the failure and offers a
 * retry instead of a blank page; the nested routes stay hidden so the stale identity is never
 * exposed.
 */
export function ReauthGate(): JSX.Element | null {
  const { signinRedirect } = useAuth();
  const [redirecting] = React.useState(() => hasReauthMarker());
  const [failed, setFailed] = React.useState(false);
  const handled = React.useRef(false);

  const requestSignin = React.useCallback((): void => {
    setFailed(false);
    // The executor runs synchronously, so a synchronous throw is handled like a rejection.
    new Promise<void>((resolve) => resolve(signinRedirect())).catch(
      (error: unknown) => {
        console.error("The sign-in request could not be started:", error);
        setFailed(true);
      }
    );
  }, [signinRedirect]);

  React.useLayoutEffect(() => {
    if (!redirecting || handled.current) {
      return;
    }
    handled.current = true;
    discardCachedOidcSession();
    stripReauthMarker();
    requestSignin();
  }, [redirecting, requestSignin]);

  if (!redirecting) {
    return <Outlet />;
  }

  if (!failed) {
    return null;
  }

  return (
    <div role="alert" style={{ padding: 24, fontFamily: "sans-serif" }}>
      <p>Sign-in could not be started.</p>
      <button type="button" onClick={requestSignin}>
        Try again
      </button>{" "}
      <button type="button" onClick={(): void => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}
